import { atom, useAtom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { useCallback } from 'react';
import type * as Dialogs from '~/components/dialogs';

export type DialogKey = keyof typeof Dialogs;

export type DialogProps = {
  [K in keyof typeof Dialogs]: Parameters<(typeof Dialogs)[K]>[0];
};

/** Return data from any API call */
export type StateData = Record<string, unknown> | null;

export interface DialogState<DialogProps = Record<string, unknown>> {
  /** The dialog's file name will act as the ID */
  id: DialogKey;
  /** Determines if the Dialog is open */
  open: boolean;
  /** Forces dialog contents to remount for each open payload */
  renderKey: number;
  /** The props to be passed to the Dialog */
  props: DialogProps;
}

export const dialogFamilyAtom = atomFamily(
  (id: DialogKey) => atom<DialogState>({ id, open: false, renderKey: 0, props: {} }),
  (a, b) => a === b,
);

export const dialogKeysAtom = atom<DialogKey[]>([]);
export const dialogStateDataAtom = atom<StateData>(null);

export const useDialogHandlers = <K extends DialogKey>(id: K) => {
  const [_dialog, updateDialog] = useAtom(dialogFamilyAtom(id));

  const handleOpenDialog = useCallback(
    (props: DialogProps[K]) => {
      updateDialog((prev) => ({ id, open: true, renderKey: prev.renderKey + 1, props: props ? { ...props } : {} }));
    },
    [id, updateDialog],
  );

  const handleCloseDialog = useCallback(() => {
    updateDialog((prev) => ({ ...prev, open: false }));
  }, [updateDialog]);

  return { handleCloseDialog, handleOpenDialog };
};
